import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  businessDateInZone,
  epochMsOf,
  instantFromEpochMs,
  instantFromISOString,
  systemClock,
  toISOString,
} from '@sakhtar/calendar';
import {
  V2Card,
  V2Badge,
  V2Button,
  V2DataGrid,
  V2Tabs,
  V2EmptyState,
  V2FormGrid,
  V2FormField,
  V2FormActions,
  LoadingState,
  StatePanel,
  type V2BadgeTone,
  V2CloseButton,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { fieldErrorsFrom, bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import type { Activity, ActivityListResponse } from '../../lib/types/crm.js';
import { ACTIVITY_TYPE_OPTIONS } from '../../lib/types/crm.js';
import { JalaliDateTimeInput } from '../../lib/date/JalaliDateTimeInput.js';

const ACTIVITY_FIELD_MESSAGES = {
  title: 'موضوع فعالیت را وارد کنید.',
};

type EffectiveStatus = 'باز' | 'انجام‌شده' | 'لغو شده' | 'عقب‌افتاده';

const STATUS_TONE: Record<EffectiveStatus, V2BadgeTone> = {
  باز: 'neutral',
  انجام‌شده: 'success',
  'لغو شده': 'neutral',
  عقب‌افتاده: 'danger',
};

/** Real dueAt vs. now comparison — "عقب‌افتاده" is not a backend status, it
 * is a real, computed presentation over the real dueAt field (the board's
 * own §04 text: 3 real statuses — باز/انجام‌شده/عقب‌افتاده — each with a
 * plain text badge). */
function dueBusinessDate(dueAt: string): string | null {
  try {
    return businessDateInZone(instantFromISOString(dueAt));
  } catch {
    return dueAt.slice(0, 10) || null;
  }
}

function effectiveStatus(a: Activity): EffectiveStatus {
  if (a.statusLabel !== 'باز') return a.statusLabel as EffectiveStatus;
  if (a.dueAt && a.dueAt < toISOString(systemClock.now())) return 'عقب‌افتاده';
  return 'باز';
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Sales CRM.dc.html §04 SALES ACTIVITIES
 * archetype: ENTITY_LIST
 * status: KIT_CONFORMANT — "عقب‌افتاده" is computed client-side from the
 *   real `dueAt` field, not a backend status; "مرتبط با" shows the real
 *   leadRef/partyRef when present. The board's own text says the
 *   activity reminder is intent only ("تا زمانی که سازوکار اعلان بسته
 *   نشده، رابط هیچ وعدهٔ تحویل نمی‌دهد") — no external notification is
 *   claimed here either, matching that.
 * state reference: ready · empty · permission_denied · network_error
 */
export function ActivitiesPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';

  const [tab, setTab] = useState<'today' | 'overdue' | 'week' | 'done'>('today');
  const [typeFilter, setTypeFilter] = useState('');
  const [query, setQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [busyRef, setBusyRef] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [activityType, setActivityType] = useState<string>('call');
  const [dueAt, setDueAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [completionTarget, setCompletionTarget] = useState<Activity | null>(null);
  const [completionResult, setCompletionResult] = useState('');
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpTitle, setFollowUpTitle] = useState('');
  const [followUpDueAt, setFollowUpDueAt] = useState('');

  const activities = useApiGet<ActivityListResponse>(
    slug ? `${base}/api/v1/crm/activities` : null,
    [slug, refreshKey],
  );

  const rows = useMemo(() => {
    if (activities.status !== 'ready') return [];
    const now = systemClock.now();
    const todayStr = systemClock.today();
    const weekAhead = toISOString(instantFromEpochMs(epochMsOf(now) + 7 * 86_400_000));

    return activities.data.activities.filter((a) => {
      const eff = effectiveStatus(a);
      if (typeFilter && a.activityTypeLabel !== typeFilter) return false;
      if (query.trim() && !a.title.toLowerCase().includes(query.trim().toLowerCase())) return false;
      if (tab === 'done') return eff === 'انجام‌شده' || eff === 'لغو شده';
      if (tab === 'overdue') return eff === 'عقب‌افتاده';
      if (tab === 'today')
        return (
          eff !== 'انجام‌شده' &&
          eff !== 'لغو شده' &&
          ((a.dueAt !== null && dueBusinessDate(a.dueAt) === todayStr) || eff === 'عقب‌افتاده')
        );
      if (tab === 'week')
        return eff !== 'انجام‌شده' && eff !== 'لغو شده' && a.dueAt !== null && a.dueAt <= weekAhead;
      return true;
    });
  }, [activities, tab, typeFilter, query]);

  const typeOptions = useMemo(() => {
    if (activities.status !== 'ready') return [];
    return [...new Set(activities.data.activities.map((a) => a.activityTypeLabel))];
  }, [activities]);

  if (!slug) return null;

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setCreateError(null);
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setFieldErrors({ title: ACTIVITY_FIELD_MESSAGES.title });
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      await apiPost(`${base}/api/v1/crm/activities`, {
        title: trimmedTitle,
        activityType,
        dueAt: dueAt || undefined,
      });
      setTitle('');
      setDueAt('');
      setRefreshKey((k) => k + 1);
    } catch (error) {
      setFieldErrors(fieldErrorsFrom(error, ACTIVITY_FIELD_MESSAGES));
      setCreateError(bannerMessageFrom(error, 'ثبت فعالیت ناموفق بود.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onCancel(ref: string) {
    setBusyRef(ref);
    try {
      await apiPost(`${base}/api/v1/crm/activities/${ref}/cancel`);
      setRefreshKey((k) => k + 1);
    } finally {
      setBusyRef(null);
    }
  }

  function closeCompletionDialog() {
    setCompletionTarget(null);
    setCompletionResult('');
    setCompletionError(null);
    setCreateFollowUp(false);
    setFollowUpTitle('');
    setFollowUpDueAt('');
  }

  async function onComplete(event: FormEvent) {
    event.preventDefault();
    if (!completionTarget) return;
    if (!completionResult.trim()) {
      setCompletionError('نتیجه فعالیت را وارد کنید.');
      return;
    }
    if (createFollowUp && !followUpTitle.trim()) {
      setCompletionError('عنوان پیگیری بعدی را وارد کنید.');
      return;
    }
    if (createFollowUp && !followUpDueAt) {
      setCompletionError('موعد پیگیری بعدی را وارد کنید.');
      return;
    }

    setBusyRef(completionTarget.ref);
    setCompletionError(null);
    try {
      await apiPost(`${base}/api/v1/crm/activities/${completionTarget.ref}/complete`, {
        result: completionResult.trim(),
        ...(createFollowUp
          ? {
              followUp: {
                title: followUpTitle.trim(),
                dueAt: followUpDueAt,
              },
            }
          : {}),
      });
      closeCompletionDialog();
      setRefreshKey((k) => k + 1);
    } catch (error) {
      setCompletionError(bannerMessageFrom(error, 'ثبت نتیجه فعالیت ناموفق بود.'));
    } finally {
      setBusyRef(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <form
          noValidate
          onSubmit={onCreate}
          style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <V2FormGrid>
            <V2FormField label="عنوان" required width="wide" error={fieldErrors.title}>
              <input
                className="sk-input"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (fieldErrors.title) setFieldErrors({});
                }}
              />
            </V2FormField>
            <V2FormField label="نوع" width="medium">
              <select
                className="sk-select"
                value={activityType}
                onChange={(e) => setActivityType(e.target.value)}
              >
                {ACTIVITY_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="موعد" width="wide">
              <JalaliDateTimeInput value={dueAt} onChange={setDueAt} />
            </V2FormField>
          </V2FormGrid>

          <V2FormActions>
            {createError ? (
              <span style={{ fontSize: 13, color: 'var(--sk-danger)', marginInlineEnd: 'auto' }}>
                {createError}
              </span>
            ) : null}
            <V2Button type="submit" variant="primary" icon="plus" disabled={submitting}>
              {submitting ? 'در حال ثبت…' : 'فعالیت جدید'}
            </V2Button>
          </V2FormActions>
        </form>
      </V2Card>

      <V2Tabs
        activeId={tab}
        items={[
          { id: 'today', label: 'امروز' },
          { id: 'overdue', label: 'عقب‌افتاده' },
          { id: 'week', label: 'این هفته' },
          { id: 'done', label: 'انجام‌شده' },
        ]}
        onChange={(id) => setTab(id as typeof tab)}
      />

      {activities.status === 'loading' ? <LoadingState /> : null}
      {activities.status === 'error' ? (
        <StatePanel
          variant={activities.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={activities.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {activities.status === 'ready' ? (
        <V2DataGrid
          icon="tasks"
          title="فعالیت‌ها"
          subtitle={`${rows.length.toLocaleString('fa-IR')} فعالیت در دسترس شما`}
          search={{ value: query, onChange: setQuery, placeholder: 'جست‌وجو در فعالیت‌ها' }}
          filters={
            typeOptions.length > 0
              ? [
                  {
                    id: 'type',
                    label: 'نوع',
                    value: typeFilter,
                    onChange: setTypeFilter,
                    options: [
                      { value: '', label: 'همه' },
                      ...typeOptions.map((t) => ({ value: t, label: t })),
                    ],
                  },
                ]
              : []
          }
          columns={[
            {
              key: 'type',
              header: 'نوع',
              width: 110,
              render: (a: Activity) => <span className="sk-tag">{a.activityTypeLabel}</span>,
            },
            {
              key: 'related',
              header: 'مرتبط با',
              width: 180,
              render: (a: Activity) => {
                const related = a.leadRef
                  ? { kind: 'lead' as const, ref: a.leadRef, text: `سرنخ — ${a.leadRef}` }
                  : a.partyRef
                    ? { kind: 'customer' as const, ref: a.partyRef, text: `مشتری — ${a.partyRef}` }
                    : null;
                return related ? (
                  <span
                    className="ltr sk-num"
                    style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}
                  >
                    <PartyLink base={base} kind={related.kind} partyRef={related.ref} inRow>
                      {related.text}
                    </PartyLink>
                  </span>
                ) : (
                  <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>
                );
              },
            },
            {
              key: 'title',
              header: 'موضوع',
              width: 220,
              wrap: true,
              render: (a: Activity) => {
                const eff = effectiveStatus(a);
                return (
                  <span
                    style={
                      eff === 'انجام‌شده' || eff === 'لغو شده'
                        ? { color: 'var(--sk-fg-muted)', textDecoration: 'line-through' }
                        : { fontWeight: 500 }
                    }
                  >
                    {a.title}
                  </span>
                );
              },
            },
            {
              key: 'due',
              header: 'موعد',
              width: 140,
              render: (a: Activity) => (
                <span className="sk-num">
                  {a.dueAt ? a.dueAt.replace('T', ' ').slice(0, 16) : '—'}
                </span>
              ),
            },
            {
              key: 'status',
              header: 'وضعیت',
              width: 120,
              render: (a: Activity) => {
                const eff = effectiveStatus(a);
                return <V2Badge tone={STATUS_TONE[eff]}>{eff}</V2Badge>;
              },
            },
            {
              key: 'actions',
              header: '',
              width: 150,
              render: (a: Activity) => {
                const eff = effectiveStatus(a);
                return eff === 'باز' || eff === 'عقب‌افتاده' ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <V2Button
                      size="sm"
                      variant="primary"
                      disabled={busyRef === a.ref}
                      onClick={() => setCompletionTarget(a)}
                    >
                      انجام‌شده
                    </V2Button>
                    <V2Button
                      size="sm"
                      variant="ghost"
                      disabled={busyRef === a.ref}
                      onClick={() => void onCancel(a.ref)}
                    >
                      لغو
                    </V2Button>
                  </div>
                ) : null;
              },
            },
          ]}
          rows={rows}
          rowKey={(a) => a.ref}
          rowTone={(a) => (effectiveStatus(a) === 'عقب‌افتاده' ? 'danger' : undefined)}
          legend={[{ tone: 'danger', label: 'عقب‌افتاده' }]}
          legendNote="فعالیت‌های موعددار فقط در همین فهرست دیده می‌شوند. ارسال یادآور بیرونی در این نسخه فعال نیست."
          emptyState={
            <V2EmptyState
              icon="tasks"
              title="فعالیتی برای نمایش نیست"
              description="فعالیت جدید (تماس، جلسه یا پیگیری) را از فرم بالا ثبت کنید."
              compact
            />
          }
        />
      ) : null}

      {completionTarget ? (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'grid',
            placeItems: 'center',
            padding: 16,
            background: 'rgb(0 0 0 / 35%)',
          }}
        >
          <V2Card>
            <form
              role="dialog"
              aria-modal="true"
              aria-labelledby="activity-completion-title"
              noValidate
              onSubmit={onComplete}
              style={{
                width: 'min(560px, calc(100vw - 32px))',
                maxHeight: 'calc(100vh - 32px)',
                overflow: 'auto',
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
              >
                <V2CloseButton onClick={closeCompletionDialog} />
              </div>
              <div>
                <h2 id="activity-completion-title" style={{ margin: 0, fontSize: 18 }}>
                  ثبت نتیجه فعالیت
                </h2>
                <p style={{ margin: '6px 0 0', color: 'var(--sk-fg-muted)', fontSize: 13 }}>
                  {completionTarget.title}
                </p>
              </div>

              <V2FormField label="نتیجه" required error={completionError ?? undefined}>
                <textarea
                  className="sk-input"
                  rows={4}
                  value={completionResult}
                  onChange={(event) => {
                    setCompletionResult(event.target.value);
                    if (completionError) setCompletionError(null);
                  }}
                  placeholder="خلاصه نتیجه تماس، جلسه یا پیگیری"
                  autoFocus
                />
              </V2FormField>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={createFollowUp}
                  onChange={(event) => setCreateFollowUp(event.target.checked)}
                />
                پیگیری بعدی ساخته شود
              </label>

              {createFollowUp ? (
                <V2FormGrid>
                  <V2FormField label="عنوان پیگیری بعدی" required width="wide">
                    <input
                      className="sk-input"
                      value={followUpTitle}
                      onChange={(event) => setFollowUpTitle(event.target.value)}
                    />
                  </V2FormField>
                  <V2FormField label="موعد پیگیری بعدی" required width="wide">
                    <JalaliDateTimeInput value={followUpDueAt} onChange={setFollowUpDueAt} />
                  </V2FormField>
                </V2FormGrid>
              ) : null}

              <p style={{ margin: 0, color: 'var(--sk-fg-muted)', fontSize: 12 }}>
                نتیجه در تاریخچه همین فعالیت ثبت می‌شود. پیگیری جدید، ارتباط و مسئول فعالیت فعلی را
                حفظ می‌کند.
              </p>

              <V2FormActions>
                <V2Button
                  type="button"
                  variant="ghost"
                  disabled={busyRef === completionTarget.ref}
                  onClick={closeCompletionDialog}
                >
                  انصراف
                </V2Button>
                <V2Button
                  type="submit"
                  variant="primary"
                  disabled={busyRef === completionTarget.ref}
                >
                  {busyRef === completionTarget.ref ? 'در حال ثبت…' : 'ثبت نتیجه'}
                </V2Button>
              </V2FormActions>
            </form>
          </V2Card>
        </div>
      ) : null}
    </div>
  );
}
